export interface ThemeConfig {
  primaryColor?: string
  secondaryColor?: string
  accentColor?: string
  fontFamily?: string
  borderRadius?: string
  mode?: 'light' | 'dark' | 'auto'
}

export interface BrandingConfig {
  primaryColor: string
  secondaryColor: string
  accentColor: string | null
  logoUrl: string | null
  template: string
}

/**
 * theme-radius som gyldig CSS-længde. Et rent tal (fx "7", som studier skriver
 * i widget-byggerens felt) er ugyldig CSS uden enhed, så hjørnerne blev stående
 * på skabelonens værdi; det tolkes nu som pixels. "0" er gyldig og bevares.
 * Kun et tal med px/rem/em/% (eller intet) slipper igennem — en hvidliste, så
 * intet andet (kommentarer, `;`, `}` …) kan sprænge CSS-blokken. Andet ignoreres,
 * og skabelonens hjørner gælder.
 */
export function normalizeThemeRadius(value: string | null | undefined): string | undefined {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : ''
  const m = /^(\d*\.?\d+)(px|rem|em|%)?$/.exec(v)
  if (!m) return undefined
  if (Number(m[1]) === 0) return '0'
  return m[2] ? v : `${m[1]}px`
}

/**
 * Generate CSS custom properties from theme config
 */
export function generateThemeStyles(theme: ThemeConfig, branding?: BrandingConfig): string {
  const primary = theme.primaryColor || branding?.primaryColor || '#10b981'
  const font = theme.fontFamily || 'Inter, system-ui, sans-serif'
  const themeRadius = normalizeThemeRadius(theme.borderRadius)
  const radius = themeRadius || '0.5rem'
  const mode = theme.mode || 'light'

  // Generate color shades
  const shades = generateColorShades(primary)

  // theme-secondary = andet stop i brand-gradienterne (billed-fallbacks, «Populær»-
  // pillen); theme-accent = fremhævninger (den populære plans ramme, «i dag» i
  // ugekalenderen) — samme roller som på studiets egen side. Uden attributten er
  // standarden præcis det widgetten altid har vist (mørk primær-nuance / primær),
  // så et eksisterende embed ikke skifter udseende. Studiets egne sekundær-/
  // accentfarver hentes bevidst IKKE ind her af samme grund.
  const secondary = theme.secondaryColor || shades[900]
  const accent = theme.accentColor || primary
  const accentShades = theme.accentColor ? generateColorShades(accent) : shades

  // theme-radius skal vinde over skabelonens hjørner. --yoga-radius sættes også
  // på .yoga-widget og pr. skabelon i styles/base.ts, og en værdi der sættes på en
  // efterkommer vinder over :host — så attributten gjorde intet. Samme greb som
  // dark-blokken nedenfor: .yoga-widget[data-template] har samme specificitet som
  // en konkret skabelon og indsættes efter base-styles. Uden attributten bestemmer
  // skabelonen som før.
  const radiusOverride = themeRadius
    ? `
    .yoga-widget,
    .yoga-widget[data-template] {
      --yoga-radius: ${themeRadius};
    }`
    : ''

  // Dark-mode-overrides. :host saettes ALTID til de lyse vaerdier nedenfor; de
  // moerke vaerdier overstyrer bagefter — enten ubetinget (mode='dark') eller
  // under vaertens OS-praeference (mode='auto', via @media prefers-color-scheme).
  // 'auto' er rent CSS: ingen JS, og den skifter selv naar vaerten/brugeren gaar
  // mellem lys og moerk. Kortenes baggrund kommer fra template-laget (styles/
  // base.ts), som kun kender lyse toner: uden .yoga-widget-override blev
  // holdnavne lys tekst paa HVIDT kort = usynlige i moerk tilstand (verificeret
  // staging 01-08-2026). .yoga-widget[data-template] har samme specificitet som
  // en konkret template og indsaettes efter base-styles, saa den vinder paa
  // kilderaekkefoelge.
  const darkBlock = `
    :host {
      --yoga-bg: #1f2937;
      --yoga-bg-secondary: #374151;
      --yoga-text: #f9fafb;
      --yoga-text-secondary: #d1d5db;
      --yoga-border: #4b5563;
    }
    .yoga-widget,
    .yoga-widget[data-template] {
      --yoga-card-bg: #1f2937;
      --yoga-card-border: #4b5563;
      --yoga-text: #f9fafb;
      --yoga-text-muted: #9ca3af;
    }`

  const darkRules =
    mode === 'dark'
      ? darkBlock
      : mode === 'auto'
        ? `@media (prefers-color-scheme: dark) {${darkBlock}
    }`
        : ''

  return `
    :host {
      --yoga-primary: ${primary};
      --yoga-primary-50: ${shades[50]};
      --yoga-primary-100: ${shades[100]};
      --yoga-primary-200: ${shades[200]};
      --yoga-primary-300: ${shades[300]};
      --yoga-primary-400: ${shades[400]};
      --yoga-primary-500: ${shades[500]};
      --yoga-primary-600: ${shades[600]};
      --yoga-primary-700: ${shades[700]};
      --yoga-primary-800: ${shades[800]};
      --yoga-primary-900: ${shades[900]};

      --yoga-secondary: ${secondary};
      --yoga-accent: ${accent};
      --yoga-accent-50: ${accentShades[50]};
      --yoga-font: ${font};
      --yoga-radius: ${radius};

      /* Lys tilstand (standard); dark/auto overstyrer nedenfor */
      --yoga-bg: #ffffff;
      --yoga-bg-secondary: #f3f4f6;
      --yoga-text: #111827;
      --yoga-text-secondary: #6b7280;
      --yoga-border: #e5e7eb;

      font-family: var(--yoga-font);
      color: var(--yoga-text);
    }

    ${radiusOverride}

    ${darkRules}
  `
}

/**
 * Generate a scale of color shades from a base color
 */
function generateColorShades(baseColor: string): Record<number, string> {
  const rgb = hexToRgb(baseColor)
  if (!rgb) {
    return {
      50: baseColor,
      100: baseColor,
      200: baseColor,
      300: baseColor,
      400: baseColor,
      500: baseColor,
      600: baseColor,
      700: baseColor,
      800: baseColor,
      900: baseColor,
    }
  }

  return {
    50: rgbToHex(lighten(rgb, 0.95)),
    100: rgbToHex(lighten(rgb, 0.9)),
    200: rgbToHex(lighten(rgb, 0.75)),
    300: rgbToHex(lighten(rgb, 0.6)),
    400: rgbToHex(lighten(rgb, 0.3)),
    500: baseColor,
    600: rgbToHex(darken(rgb, 0.1)),
    700: rgbToHex(darken(rgb, 0.25)),
    800: rgbToHex(darken(rgb, 0.4)),
    900: rgbToHex(darken(rgb, 0.55)),
  }
}

interface RGB {
  r: number
  g: number
  b: number
}

function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

function rgbToHex(rgb: RGB): string {
  return `#${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`
}

function componentToHex(c: number): string {
  const hex = Math.round(Math.max(0, Math.min(255, c))).toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

function lighten(rgb: RGB, amount: number): RGB {
  return {
    r: rgb.r + (255 - rgb.r) * amount,
    g: rgb.g + (255 - rgb.g) * amount,
    b: rgb.b + (255 - rgb.b) * amount,
  }
}

function darken(rgb: RGB, amount: number): RGB {
  return {
    r: rgb.r * (1 - amount),
    g: rgb.g * (1 - amount),
    b: rgb.b * (1 - amount),
  }
}
